/**
 * Lesson content banks.
 * ---------------------------------------------------------------------------
 * Every bank is plain data. The generator (lib/lessonContent.ts) filters a bank
 * down to the keys a child has actually been taught, so a Level 6 exercise can
 * never contain a letter the child has not met. That constraint is what makes
 * the progression feel fair instead of punishing.
 *
 * Vocabulary is deliberately concrete and age 5–14 friendly: nouns a child can
 * picture, no abstractions, nothing that needs explaining.
 */

/* ─────────────────────────────────────────────────────────── word bank ──── */

/**
 * Ordered roughly by length. The generator prefers short words early in a
 * level and lets longer ones in as the child progresses.
 */
export const WORD_BANK: string[] = [
  // Home-row reachable (a s d f g h j k l) — everything Levels 1–6 can use.
  // This block is deliberately exhaustive rather than illustrative: it is
  // genuinely close to every common English word spellable without leaving the
  // home row, and Level 6 has nothing else to draw on.
  'as',
  'ad',
  'add',
  'all',
  'ask',
  'asks',
  'dad',
  'fad',
  'lad',
  'lads',
  'sad',
  'fall',
  'falls',
  'flask',
  'salad',
  'salads',
  'lass',
  'adds',
  'flak',
  'alfalfa',
  'gas',
  'gash',
  'glad',
  'glass',
  'flag',
  'flags',
  'flash',
  'half',
  'hall',
  'halls',
  'had',
  'has',
  'hash',
  'lash',
  'dash',
  'sash',
  'shall',
  'saga',
  'gala',
  'slash',
  'salsa',
  'shag',
  'gaff',
  'aghast',
  // + top row (q w e r t y u i o p)
  'at',
  'up',
  'us',
  'it',
  'is',
  'if',
  'to',
  'do',
  'so',
  'or',
  'we',
  'he',
  'the',
  'top',
  'toy',
  'toys',
  'tip',
  'top',
  'pot',
  'pit',
  'put',
  'pup',
  'try',
  'true',
  'quit',
  'quiet',
  'quite',
  'water',
  'paper',
  'purple',
  'player',
  'party',
  'pirate',
  'rooster',
  'trip',
  'star',
  'start',
  'squirrel',
  'yellow',
  'writer',
  'toaster',
  'tulip',
  'lizard',
  'toy',
  'jelly',
  'kite',
  'that',
  'this',
  'they',
  'their',
  'tiger',
  'turtle',
  'seal',
  'otter',
  'title',
  'apple',
  'apples',
  'street',
  // + bottom row (z x c v b n m) — the full alphabet
  'cat',
  'cats',
  'dog',
  'dogs',
  'bee',
  'bees',
  'cow',
  'cows',
  'fox',
  'foxes',
  'ant',
  'ants',
  'bat',
  'bats',
  'owl',
  'owls',
  'pig',
  'pigs',
  'hen',
  'hens',
  'frog',
  'frogs',
  'bear',
  'bears',
  'duck',
  'ducks',
  'fish',
  'goat',
  'goats',
  'lion',
  'lions',
  'mouse',
  'horse',
  'sheep',
  'snake',
  'whale',
  'zebra',
  'panda',
  'koala',
  'camel',
  'robin',
  'shark',
  'moose',
  'llama',
  'gecko',
  'rabbit',
  'monkey',
  'parrot',
  'dragon',
  'dolphin',
  'penguin',
  'octopus',
  'red',
  'blue',
  'green',
  'pink',
  'black',
  'white',
  'brown',
  'orange',
  'one',
  'two',
  'six',
  'ten',
  'nine',
  'four',
  'five',
  'three',
  'seven',
  'eight',
  'sun',
  'moon',
  'star',
  'sky',
  'rain',
  'snow',
  'wind',
  'cloud',
  'storm',
  'tree',
  'leaf',
  'seed',
  'root',
  'grass',
  'flower',
  'garden',
  'forest',
  'cake',
  'milk',
  'bread',
  'honey',
  'lunch',
  'pizza',
  'grape',
  'lemon',
  'berry',
  'peach',
  'melon',
  'carrot',
  'banana',
  'cookie',
  'muffin',
  'book',
  'pen',
  'desk',
  'bag',
  'map',
  'ruler',
  'chair',
  'crayon',
  'pencil',
  'school',
  'teacher',
  'friend',
  'family',
  'brother',
  'sister',
  'people',
  'ball',
  'bike',
  'boat',
  'kite',
  'drum',
  'robot',
  'puzzle',
  'rocket',
  'castle',
  'bridge',
  'island',
  'planet',
  'jungle',
  'meadow',
  'river',
  'run',
  'jump',
  'walk',
  'swim',
  'read',
  'sing',
  'draw',
  'play',
  'help',
  'build',
  'climb',
  'dance',
  'laugh',
  'share',
  'think',
  'learn',
  'smile',
  'happy',
  'brave',
  'kind',
  'quick',
  'quiet',
  'silly',
  'sunny',
  'tiny',
  'big',
  'small',
  'fast',
  'slow',
  'warm',
  'cold',
  'soft',
  'loud',
  'bright',
  'today',
  'night',
  'morning',
  'summer',
  'winter',
  'spring',
  'autumn',
  'and',
  'but',
  'for',
  'you',
  'not',
  'can',
  'was',
  'are',
  'has',
  'had',
  'with',
  'from',
  'have',
  'like',
  'when',
  'what',
  'were',
  'said',
  'take',
  'because',
  'about',
  'again',
  'every',
  'never',
  'always',
  'together',
].filter((w) => /^[a-z]+$/.test(w));

/* ───────────────────────────────────────────────────── sentence banks ──── */

/** Lowercase, no punctuation — usable as soon as the alphabet is complete. */
export const PLAIN_SENTENCES: string[] = [
  'the cat sat on a warm mat',
  'my dog can jump over the log',
  'we build a big sand castle',
  'a small bird sings in the tree',
  'the sun is bright and warm today',
  'six frogs hop across the pond',
  'she reads a book about dragons',
  'the red kite flies above the park',
  'we plant seeds in the garden',
  'a brave mouse found the cheese',
  'ten ducks swim in the blue lake',
  'the quick brown fox jumps over the lazy dog',
  'my friend draws a happy robot',
  'we ride our bikes to the river',
  'the baby panda eats green bamboo',
];

/** Full sentences with capitals and punctuation — Level 11 and beyond. */
export const SENTENCES: string[] = [
  'The quick brown fox jumps over the lazy dog.',
  'Pack my box with five dozen jugs of liquid.',
  'How vexingly quick daft zebras jump!',
  'My family walks to the park every Saturday.',
  'Do you want to build a rocket with me?',
  'The elephant sprayed water on the happy children.',
  'Zara found a shiny blue stone near the river.',
  'We baked twelve muffins for the school picnic.',
  'Can you name every planet in our solar system?',
  'The old lighthouse blinked through the thick fog.',
  'Jack quickly explained why the moon glows at night.',
  'Our teacher said practice makes progress, not perfection.',
  'A curious penguin waddled toward the camera.',
  'She whispered, "Look at the shooting star!"',
  'Seventeen ants marched across the kitchen floor.',
  'The library opens at nine and closes at six.',
  'Bright yellow tulips grew beside the wooden fence.',
  'He typed his very first sentence without looking down.',
  'Nobody expected the tiny seed to become a giant tree.',
  'Every expert was once a beginner who kept going.',
];

/** Multi-sentence passages — Level 12 endurance work. */
export const PARAGRAPHS: string[] = [
  'Honey bees are tiny but they do enormous work. A single hive can visit two million flowers to make one jar of honey. When a bee finds a good patch of flowers, it flies home and dances in a figure eight to tell the others exactly where to go.',
  'The ocean covers more than seventy percent of our planet, yet we have explored only a small part of it. Scientists believe millions of species are still waiting to be discovered down there in the cold, dark water.',
  'Long ago, people wrote letters by hand and waited weeks for a reply. Today a message crosses the world in less than a second. The keyboard you are using right now is one of the reasons that change was possible.',
  'A rainbow appears when sunlight passes through raindrops. Each drop bends the light and splits it into seven colours: red, orange, yellow, green, blue, indigo and violet. You can only see a rainbow when the sun is behind you and the rain is in front.',
  'Practice works best in small, regular pieces. Ten focused minutes every day will teach your fingers more than two hours once a week. Your brain builds the pathway while you sleep, so tomorrow always starts a little easier than today.',
  'The tallest tree ever measured was a coast redwood in California, standing over one hundred and fifteen metres high. It was already growing before the first printing press was built, and it is still growing today, adding a little more height every single year.',
  'Octopuses have three hearts and blue blood. Two of the hearts pump blood to the gills, while the third pumps it around the rest of the body. Strangely, that third heart stops beating whenever the octopus swims, which is one reason they usually prefer to crawl.',
  'A single lightning bolt heats the air around it to roughly thirty thousand degrees Celsius, which is about five times hotter than the surface of the sun. The air expands so violently that it produces the crack of thunder you hear a moment later.',
  'The QWERTY keyboard you are typing on was designed in the eighteen seventies for mechanical typewriters. Common letter pairs were separated so the metal arms would not jam. The jamming problem disappeared long ago, but the layout stayed, and now billions of people know it by heart.',
  "Antarctica is technically a desert. It receives so little precipitation that some of its dry valleys have not seen rain for nearly two million years. Ninety percent of the world's ice sits there anyway, frozen from snow that fell across thousands of centuries.",
];

/** Short stories, typed one paragraph per screen — Level 13. */
export interface StoryDef {
  id: string;
  title: string;
  emoji: string;
  parts: string[];
}

export const STORIES: StoryDef[] = [
  {
    id: 'lighthouse',
    title: 'The Lighthouse Cat',
    emoji: '🐈',
    parts: [
      'On a rocky island at the edge of the sea stood an old white lighthouse. Inside lived a keeper named Ida and a grey cat named Pepper.',
      'One stormy night the great lamp went dark. Ida climbed the spiral stairs, but the gears had jammed and she could not turn them alone.',
      'Pepper squeezed into the narrow gap and pushed. Slowly, the lamp began to spin again, and far out in the black water, a fishing boat found its way home.',
    ],
  },
  {
    id: 'seed',
    title: 'The Seed That Waited',
    emoji: '🌱',
    parts: [
      'A small seed fell into a crack in the pavement. It was dark down there, and nothing seemed to happen for a very long time.',
      'Rain came, then sun, then rain again. The seed pushed out one thin root, then a second, then a pale green shoot no thicker than a thread.',
      'Two springs later, people walked past a young tree growing straight out of the stone, and nobody could believe it had started so small.',
    ],
  },
  {
    id: 'robot',
    title: 'Bolt Learns to Type',
    emoji: '🤖',
    parts: [
      'Bolt was a robot with ten shiny fingers and absolutely no idea what to do with them. Every time he tried to write a message, he pressed all the keys at once.',
      'His friend Mira showed him the home row. "Put these four fingers here," she said, "and these four here. Feel the little bumps? Those are your anchors."',
      'Bolt practised for ten minutes a day. By the end of the month he wrote Mira a letter, and he did not look down at the keyboard even once.',
    ],
  },
];

/* ───────────────────────────────────────────────────── numbers & symbols ── */

export const NUMBER_PHRASES: string[] = [
  'I have 3 cats and 12 fish.',
  'The bus leaves at 7 and arrives at 9.',
  'She scored 48 points in 2 games.',
  'Add 15 and 27 to get 42.',
  'My birthday is on the 6th of May in 2019.',
  'There are 365 days in a year and 24 hours in a day.',
  'Room 108 is on floor 1, and room 250 is on floor 2.',
  'We ran 5 km in 30 minutes.',
];

export const SYMBOL_PHRASES: string[] = [
  'Email me at zara@school.org — I reply fast!',
  'Total: $12.50 (including tax).',
  'Is 5 + 7 = 12? Yes!',
  'Files: notes.txt, map_v2.png & story-final.doc',
  'Press Ctrl + S to save your work.',
  '"Well done!" said Mr. Ruiz. "That was 100% correct."',
  'Use #kids-typing when you share your score.',
  'The answer is either A, B or C — choose one.',
];

/* ──────────────────────────────────────────────────── coaching one-liners ── */

/** Rotated between exercises so the encouragement never feels canned. */
export const CHEERS: string[] = [
  'Nice work!',
  'You did it!',
  'Fingers on fire!',
  'Smooth and steady!',
  'That was great!',
  'Keep it going!',
  'Brilliant typing!',
  'You are getting quicker!',
];

/**
 * Shown only when accuracy actually needs work. Kept separate from the general
 * tips below so a child who just earned three stars is never told to slow down.
 */
export const ACCURACY_TIPS: string[] = [
  'Slow down a little — accuracy first, speed comes free.',
  'Mistakes are just practice. Go again, a bit steadier.',
];

/** Neutral technique reminders, safe to show after any run. */
export const TECHNIQUE_TIPS: string[] = [
  'Try to keep your eyes on the screen, not on your hands.',
  'Bring your fingers back to the home row after every reach.',
  'Sit tall, relax your shoulders, and breathe.',
  'Rest your fingers lightly — you do not need to press hard.',
  'Ten minutes tomorrow will do more than an hour today.',
];
